import React, { useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { FEATURE_FLAGS } from '../../config/featureFlags';
import { useLeafletMap } from '../../hooks/useLeafletMap';
import { MapsetType } from '../../config/mapyApi';

// Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface LeafletMapProps {
    /** Počáteční střed mapy [lat, lon] */
    center?: [number, number];
    /** Počáteční zoom */
    zoom?: number;
    /** Minimální zoom */
    minZoom?: number;
    /** Maximální zoom */
    maxZoom?: number;
    /** CSS třída pro kontejner */
    className?: string;
    /** Styl pro kontejner */
    style?: React.CSSProperties;
    /** Počáteční mapset */
    initialMapset?: MapsetType;
    /** Zobrazit přepínač vrstev */
    showLayerControl?: boolean;
    /** Callback při změně vrstvy */
    onLayerChange?: (layer: MapsetType) => void;
    /** Children komponenty (markery, polyline, atd.) */
    children?: React.ReactNode;
    /** Callback pro získání reference na mapu */
    whenReady?: (map: L.Map) => void;
}

/**
 * Komponenta pro interní získání reference na mapu
 */
const MapReadyHandler: React.FC<{ onReady?: (map: L.Map) => void }> = ({ onReady }) => {
    const map = useMap();

    React.useEffect(() => {
        if (onReady) {
            onReady(map);
        }
    }, [map, onReady]);

    return null;
};

/**
 * Komponenta pro opravu renderování dlaždic při změně velikosti kontejneru
 * Používá ResizeObserver pro sledování změn velikosti kontejneru
 *
 * Klíčové pro plynulý resize:
 * 1. Uložit střed a zoom mapy při začátku resize
 * 2. Průběžně volat invalidateSize během animace (requestAnimationFrame)
 * 3. Po dokončení animace obnovit přesnou pozici
 */
const InvalidateSizeHandler: React.FC = () => {
    const map = useMap();

    React.useEffect(() => {
        let resizeEndTimeout: NodeJS.Timeout | null = null;
        let animationFrameId: number | null = null;
        let isFirstResize = true;
        let savedCenter: L.LatLng | null = null;
        let savedZoom: number | null = null;
        let isResizing = false;

        // Zavolat invalidateSize po krátkém zpoždění při mountu
        const timeoutId = setTimeout(() => {
            map.invalidateSize({ pan: false });
        }, 100);

        // Funkce pro průběžné invalidování během animace
        const continuousInvalidate = () => {
            if (isResizing) {
                map.invalidateSize({ animate: false, pan: false });
                animationFrameId = requestAnimationFrame(continuousInvalidate);
            }
        };

        // ResizeObserver pro sledování změn velikosti kontejneru
        const container = map.getContainer();
        const resizeObserver = new ResizeObserver(() => {
            // Přeskočit první resize (při mountu)
            if (isFirstResize) {
                isFirstResize = false;
                return;
            }

            // Při začátku resize uložit aktuální pozici a spustit průběžné invalidování
            if (!isResizing) {
                isResizing = true;
                savedCenter = map.getCenter();
                savedZoom = map.getZoom();

                // Spustit průběžné invalidování pomocí requestAnimationFrame
                animationFrameId = requestAnimationFrame(continuousInvalidate);
            }

            // Debounce pro detekci konce resize
            if (resizeEndTimeout) {
                clearTimeout(resizeEndTimeout);
            }

            resizeEndTimeout = setTimeout(() => {
                // Zastavit průběžné invalidování
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }

                // Finální invalidace
                map.invalidateSize({ animate: false, pan: false });

                // Obnovit přesnou pozici mapy pokud byla uložena
                if (savedCenter && savedZoom !== null) {
                    map.setView(savedCenter, savedZoom, { animate: false });
                }

                // Reset stavu
                isResizing = false;
                savedCenter = null;
                savedZoom = null;
            }, 450); // Čekat na dokončení CSS transition (400ms + buffer)
        });

        resizeObserver.observe(container);

        return () => {
            clearTimeout(timeoutId);
            if (resizeEndTimeout) {
                clearTimeout(resizeEndTimeout);
            }
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            resizeObserver.disconnect();
        };
    }, [map]);

    return null;
};

/**
 * Základní Leaflet mapa s Mapy.com tile vrstvami
 *
 * Nahrazuje starý SMap SDK s react-leaflet
 *
 * @example
 * <LeafletMap
 *     center={[50.0, 15.0]}
 *     zoom={10}
 *     showLayerControl
 * >
 *     <Marker position={[50.0, 15.0]} />
 * </LeafletMap>
 */
export const LeafletMap: React.FC<LeafletMapProps> = ({
    center = [50.027429, 15.202828], // Střed ČR
    zoom = 7,
    minZoom = 7,
    maxZoom = 19,
    className = '',
    style,
    initialMapset = 'basic',
    showLayerControl = true,
    onLayerChange,
    children,
    whenReady,
}) => {
    const [activeMapset, setActiveMapset] = useState<MapsetType>(initialMapset);
    const { getTileUrl, attribution } = useLeafletMap();

    const handleLayerChange = useCallback((mapset: MapsetType) => {
        setActiveMapset(mapset);
        onLayerChange?.(mapset);
    }, [onLayerChange]);

    const mapStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
        ...style,
    }), [style]);

    // Hranice České republiky s mírným paddingem
    const czechBounds: L.LatLngBoundsExpression = [
        [48.55, 12.09],  // Jihozápad (nejjižnější a nejzápadnější bod)
        [51.06, 18.87],  // Severovýchod (nejsevernější a nejvýchodnější bod)
    ];

    return (
        <MapContainer
            center={center}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            style={mapStyle}
            className={`leaflet-map ${className}`}
            scrollWheelZoom={true}
            maxBounds={czechBounds}
            maxBoundsViscosity={1.0}  // Tvrdé omezení - nelze posunout mimo hranice
        >
            <MapReadyHandler onReady={whenReady} />
            <InvalidateSizeHandler />

            {showLayerControl ? (
                <LayersControl position="topleft">
                    <LayersControl.BaseLayer
                        checked={activeMapset === 'basic'}
                        name="Základní"
                    >
                        <TileLayer
                            url={getTileUrl('basic')}
                            attribution={attribution}
                            eventHandlers={{
                                add: () => handleLayerChange('basic'),
                            }}
                        />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer
                        checked={activeMapset === 'outdoor'}
                        name="Turistická"
                    >
                        <TileLayer
                            url={getTileUrl('outdoor')}
                            attribution={attribution}
                            eventHandlers={{
                                add: () => handleLayerChange('outdoor'),
                            }}
                        />
                    </LayersControl.BaseLayer>
                </LayersControl>
            ) : (
                <TileLayer
                    url={getTileUrl(activeMapset)}
                    attribution={attribution}
                />
            )}

            {children}
        </MapContainer>
    );
};

export default LeafletMap;
